// src/utils/dateUtils.js
// Comprehensive date utility functions with timezone support for multi-region users

import { format, parse, parseISO, isValid, addDays, addHours, differenceInDays, differenceInHours, differenceInMinutes, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

// ============================================================================
// TIMEZONE CONSTANTS
// ============================================================================

/**
 * Common timezone options grouped by region
 * IANA timezone identifiers
 */
export const TIMEZONE_OPTIONS = [
  // North America
  { value: 'America/New_York', label: 'Eastern Time (ET)', region: 'North America' },
  { value: 'America/Chicago', label: 'Central Time (CT)', region: 'North America' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', region: 'North America' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', region: 'North America' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', region: 'North America' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', region: 'North America' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)', region: 'North America' },
  { value: 'America/Toronto', label: 'Toronto (ET)', region: 'North America' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)', region: 'North America' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)', region: 'North America' },

  // South America
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', region: 'South America' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)', region: 'South America' },
  { value: 'America/Santiago', label: 'Santiago (CLT)', region: 'South America' },
  { value: 'America/Bogota', label: 'Bogotá (COT)', region: 'South America' },

  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (CET)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (CET)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', region: 'Europe' },

  // Asia
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (IST)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
  { value: 'Asia/Manila', label: 'Manila (PHT)', region: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Jakarta (WIB)', region: 'Asia' },

  // Australia & Pacific
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', region: 'Australia & Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST)', region: 'Australia & Pacific' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST, No DST)', region: 'Australia & Pacific' },
  { value: 'Australia/Perth', label: 'Perth (AWST)', region: 'Australia & Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)', region: 'Australia & Pacific' },

  // Africa & Middle East
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', region: 'Africa' },
  { value: 'Africa/Cairo', label: 'Cairo (EET)', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)', region: 'Africa' },

  // UTC
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)', region: 'Universal' },
];

/**
 * Get timezone options grouped by region
 */
export function getTimezoneOptionsGrouped() {
  const grouped = {};
  TIMEZONE_OPTIONS.forEach(tz => {
    if (!grouped[tz.region]) {
      grouped[tz.region] = [];
    }
    grouped[tz.region].push(tz);
  });
  return grouped;
}

// ============================================================================
// TIMEZONE DETECTION & MANAGEMENT
// ============================================================================

/**
 * Get the user's browser timezone
 * @returns {string} IANA timezone identifier
 */
export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Get timezone offset in hours for a given timezone
 * @param {string} timezone - IANA timezone identifier
 * @param {Date} date - Optional date to check (for DST)
 * @returns {number} Offset in hours
 */
export function getTimezoneOffset(timezone, date = new Date()) {
  try {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return (tzDate - utcDate) / (1000 * 60 * 60);
  } catch {
    return 0;
  }
}

/**
 * Get timezone abbreviation (e.g., EST, PST, UTC)
 * @param {string} timezone - IANA timezone identifier
 * @param {Date} date - Optional date to check (for DST)
 * @returns {string} Timezone abbreviation
 */
export function getTimezoneAbbreviation(timezone, date = new Date()) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart ? tzPart.value : timezone;
  } catch {
    return timezone;
  }
}

// ============================================================================
// DATE CONVERSION & PARSING
// ============================================================================

/**
 * Convert any date input to a valid Date object
 * Handles: Date objects, Firebase Timestamps, ISO strings, timestamps
 * @param {*} value - Date value in various formats
 * @returns {Date|null} Valid Date object or null
 */
export function toDate(value) {
  if (!value) return null;

  // Already a Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // Firebase Timestamp (has toDate method)
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return isNaN(date.getTime()) ? null : date;
  }

  // Number (timestamp in ms)
  if (typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  // String
  if (typeof value === 'string') {
    // YYYY-MM-DD date-only strings must be parsed as local midnight.
    // Both new Date("YYYY-MM-DD") and parseISO("YYYY-MM-DD") treat them as
    // UTC midnight per ISO 8601, which shifts the displayed date one day
    // earlier in any timezone west of UTC.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }

    // Full datetime strings — use parseISO (preserves timezone info if present)
    let date = parseISO(value);
    if (isValid(date)) return date;

    // Fallback to native parsing
    date = new Date(value);
    if (!isNaN(date.getTime())) return date;
  }

  return null;
}

/**
 * Parse date and time strings into a Date object
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @param {string} timeStr - Time string (HH:mm or HH:mm:ss)
 * @param {string} timezone - Target timezone (optional)
 * @returns {Date|null} Parsed Date object
 */
export function parseDateAndTime(dateStr, timeStr, timezone = null) {
  if (!dateStr) return null;

  const time = timeStr || '00:00';
  const isoString = `${dateStr}T${time}`;

  let date = parseISO(isoString);
  if (!isValid(date)) {
    date = new Date(isoString);
  }

  if (isNaN(date.getTime())) return null;

  // If timezone provided, adjust for it
  if (timezone) {
    return convertToTimezone(date, timezone);
  }

  return date;
}

/**
 * Convert a date from one timezone to another
 * @param {Date} date - Source date
 * @param {string} targetTimezone - Target timezone
 * @param {string} sourceTimezone - Source timezone (default: UTC)
 * @returns {Date} Converted date
 */
export function convertToTimezone(date, targetTimezone, sourceTimezone = 'UTC') {
  if (!date || !isValid(date)) return null;

  try {
    // Get the date string in target timezone
    const options = {
      timeZone: targetTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };

    const formatter = new Intl.DateTimeFormat('en-CA', options);
    const parts = formatter.formatToParts(date);

    const getPart = (type) => parts.find(p => p.type === type)?.value || '00';

    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');
    const hour = getPart('hour');
    const minute = getPart('minute');
    const second = getPart('second');

    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
  } catch {
    return date;
  }
}

/**
 * Convert a local date to UTC for storage
 * @param {Date} date - Local date
 * @param {string} timezone - Source timezone
 * @returns {Date} UTC date
 */
export function toUTC(date, timezone) {
  if (!date || !isValid(date)) return null;

  try {
    // Create a date string that represents the local time in the given timezone
    const localString = date.toLocaleString('en-US', { timeZone: timezone });
    const localDate = new Date(localString);

    // Calculate the offset
    const offset = localDate.getTime() - date.getTime();

    // Apply reverse offset to get UTC
    return new Date(date.getTime() - offset);
  } catch {
    return date;
  }
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

/**
 * Format a date for display
 * @param {*} value - Date value (Date, Timestamp, string, etc.)
 * @param {Object} options - Formatting options
 * @param {string} options.format - date-fns format string (default: 'MMM d, yyyy')
 * @param {string} options.timezone - Target timezone for display
 * @param {boolean} options.includeTime - Include time in output
 * @param {boolean} options.includeTimezone - Include timezone abbreviation
 * @returns {string} Formatted date string
 */
export function formatDate(value, options = {}) {
  const {
    format: formatStr = 'MMM d, yyyy',
    timezone = null,
    includeTime = false,
    includeTimezone = false
  } = options;

  let date = toDate(value);
  if (!date) return '—';

  // Convert to target timezone if specified
  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  let result = format(date, formatStr);

  if (includeTime) {
    result += ' ' + format(date, 'h:mm a');
  }

  if (includeTimezone && timezone) {
    result += ' ' + getTimezoneAbbreviation(timezone, date);
  }

  return result;
}

/**
 * Format a date for input fields (YYYY-MM-DD)
 * @param {*} value - Date value
 * @param {string} timezone - Target timezone
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function formatDateForInput(value, timezone = null) {
  let date = toDate(value);
  if (!date) return '';

  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  return format(date, 'yyyy-MM-dd');
}

/**
 * Format a time for input fields (HH:mm)
 * @param {*} value - Date value
 * @param {string} timezone - Target timezone
 * @returns {string} Time string in HH:mm format
 */
export function formatTimeForInput(value, timezone = null) {
  let date = toDate(value);
  if (!date) return '';

  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  return format(date, 'HH:mm');
}

/**
 * Format a date and time together
 * @param {*} value - Date value
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(value, options = {}) {
  const {
    dateFormat = 'MMM d, yyyy',
    timeFormat = 'h:mm a',
    timezone = null,
    includeTimezone = false,
    separator = ' at '
  } = options;

  let date = toDate(value);
  if (!date) return '—';

  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  let result = format(date, dateFormat) + separator + format(date, timeFormat);

  if (includeTimezone && timezone) {
    result += ' ' + getTimezoneAbbreviation(timezone, date);
  }

  return result;
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * @param {*} value - Date value
 * @param {Date} baseDate - Base date for comparison (default: now)
 * @returns {string} Relative time string
 */
export function formatRelativeTime(value, baseDate = new Date()) {
  const date = toDate(value);
  if (!date) return '—';

  const diffMs = date.getTime() - baseDate.getTime();
  const isPast = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);

  const minutes = Math.floor(absDiffMs / (1000 * 60));
  const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
  const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  let timeStr;
  if (minutes < 1) {
    timeStr = 'just now';
    return timeStr;
  } else if (minutes < 60) {
    timeStr = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (hours < 24) {
    timeStr = `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (days < 7) {
    timeStr = `${days} day${days !== 1 ? 's' : ''}`;
  } else if (weeks < 4) {
    timeStr = `${weeks} week${weeks !== 1 ? 's' : ''}`;
  } else {
    timeStr = `${months} month${months !== 1 ? 's' : ''}`;
  }

  return isPast ? `${timeStr} ago` : `in ${timeStr}`;
}

/**
 * Format duration between two dates
 * @param {*} startValue - Start date value
 * @param {*} endValue - End date value
 * @returns {string} Duration string
 */
export function formatDuration(startValue, endValue) {
  const start = toDate(startValue);
  const end = toDate(endValue);

  if (!start || !end) return '—';

  const totalMinutes = Math.abs(differenceInMinutes(end, start));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

// ============================================================================
// DATE COMPARISON & VALIDATION
// ============================================================================

/**
 * Check if a date is valid
 * @param {*} value - Date value
 * @returns {boolean}
 */
export function isValidDate(value) {
  const date = toDate(value);
  return date !== null && isValid(date);
}

/**
 * Check if a date is in the past
 * @param {*} value - Date value
 * @param {string} timezone - Timezone for comparison
 * @returns {boolean}
 */
export function isPast(value, timezone = null) {
  let date = toDate(value);
  if (!date) return false;

  let now = new Date();
  if (timezone) {
    date = convertToTimezone(date, timezone);
    now = convertToTimezone(now, timezone);
  }

  return date < now;
}

/**
 * Check if a date is in the future
 * @param {*} value - Date value
 * @param {string} timezone - Timezone for comparison
 * @returns {boolean}
 */
export function isFuture(value, timezone = null) {
  let date = toDate(value);
  if (!date) return false;

  let now = new Date();
  if (timezone) {
    date = convertToTimezone(date, timezone);
    now = convertToTimezone(now, timezone);
  }

  return date > now;
}

/**
 * Check if a date is today
 * @param {*} value - Date value
 * @param {string} timezone - Timezone for comparison
 * @returns {boolean}
 */
export function isToday(value, timezone = null) {
  let date = toDate(value);
  if (!date) return false;

  let now = new Date();
  if (timezone) {
    date = convertToTimezone(date, timezone);
    now = convertToTimezone(now, timezone);
  }

  return format(date, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
}

/**
 * Check if two dates are on the same day
 * @param {*} value1 - First date
 * @param {*} value2 - Second date
 * @param {string} timezone - Timezone for comparison
 * @returns {boolean}
 */
export function isSameDay(value1, value2, timezone = null) {
  let date1 = toDate(value1);
  let date2 = toDate(value2);

  if (!date1 || !date2) return false;

  if (timezone) {
    date1 = convertToTimezone(date1, timezone);
    date2 = convertToTimezone(date2, timezone);
  }

  return format(date1, 'yyyy-MM-dd') === format(date2, 'yyyy-MM-dd');
}

// ============================================================================
// DATE RANGES & BOUNDARIES
// ============================================================================

/**
 * Get start of day for a date
 * @param {*} value - Date value
 * @param {string} timezone - Timezone
 * @returns {Date}
 */
export function getStartOfDay(value, timezone = null) {
  let date = toDate(value);
  if (!date) return null;

  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  return startOfDay(date);
}

/**
 * Get end of day for a date
 * @param {*} value - Date value
 * @param {string} timezone - Timezone
 * @returns {Date}
 */
export function getEndOfDay(value, timezone = null) {
  let date = toDate(value);
  if (!date) return null;

  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  return endOfDay(date);
}

/**
 * Get date range for a week
 * @param {*} value - Date value within the week
 * @param {string} timezone - Timezone
 * @param {number} weekStartsOn - Day week starts (0=Sunday, 1=Monday)
 * @returns {{start: Date, end: Date}}
 */
export function getWeekRange(value, timezone = null, weekStartsOn = 0) {
  let date = toDate(value);
  if (!date) return { start: null, end: null };

  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  return {
    start: startOfWeek(date, { weekStartsOn }),
    end: endOfWeek(date, { weekStartsOn })
  };
}

/**
 * Get date range for a month
 * @param {*} value - Date value within the month
 * @param {string} timezone - Timezone
 * @returns {{start: Date, end: Date}}
 */
export function getMonthRange(value, timezone = null) {
  let date = toDate(value);
  if (!date) return { start: null, end: null };

  if (timezone) {
    date = convertToTimezone(date, timezone);
  }

  return {
    start: startOfMonth(date),
    end: endOfMonth(date)
  };
}

// ============================================================================
// STORAGE FORMAT HELPERS
// ============================================================================

/**
 * Prepare a date for Firestore storage (ISO string with timezone info)
 * @param {*} value - Date value
 * @param {string} timezone - Business timezone
 * @returns {Object} Storage object with date and timezone metadata
 */
export function prepareForStorage(value, timezone = null) {
  const date = toDate(value);
  if (!date) return null;

  return {
    iso: date.toISOString(),
    timestamp: date.getTime(),
    timezone: timezone || getBrowserTimezone(),
    utcOffset: timezone ? getTimezoneOffset(timezone, date) : new Date().getTimezoneOffset() / -60
  };
}

/**
 * Parse a stored date object back to a Date
 * @param {Object|string|Date} stored - Stored date object or value
 * @param {string} displayTimezone - Timezone for display (optional)
 * @returns {Date}
 */
export function parseFromStorage(stored, displayTimezone = null) {
  // Handle direct values
  if (!stored) return null;
  if (stored instanceof Date) return stored;
  if (typeof stored === 'string' || typeof stored === 'number') {
    return toDate(stored);
  }

  // Handle storage object
  if (stored.iso) {
    const date = parseISO(stored.iso);
    if (displayTimezone) {
      return convertToTimezone(date, displayTimezone);
    }
    return date;
  }

  if (stored.timestamp) {
    const date = new Date(stored.timestamp);
    if (displayTimezone) {
      return convertToTimezone(date, displayTimezone);
    }
    return date;
  }

  return toDate(stored);
}

// ============================================================================
// UTILITY EXPORTS (re-export date-fns utilities for convenience)
// ============================================================================

export { addDays, addHours, differenceInDays, differenceInHours, differenceInMinutes, format, parse, parseISO, isValid };

// Default export with all utilities
export default {
  // Timezone
  TIMEZONE_OPTIONS,
  getTimezoneOptionsGrouped,
  getBrowserTimezone,
  getTimezoneOffset,
  getTimezoneAbbreviation,

  // Conversion
  toDate,
  parseDateAndTime,
  convertToTimezone,
  toUTC,

  // Formatting
  formatDate,
  formatDateForInput,
  formatTimeForInput,
  formatDateTime,
  formatRelativeTime,
  formatDuration,

  // Validation
  isValidDate,
  isPast,
  isFuture,
  isToday,
  isSameDay,

  // Ranges
  getStartOfDay,
  getEndOfDay,
  getWeekRange,
  getMonthRange,

  // Storage
  prepareForStorage,
  parseFromStorage,

  // Re-exports
  addDays,
  addHours,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  parse,
  parseISO,
  isValid
};
